import { IconModalSent } from '@doltech/icons-from-dev/modal/IconModalSent';
import { figmaColors } from '@doltech/ui/lib/figma/colors';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { ConfirmDrawerModal } from '@doltech/ui/lib/figma/Mobile/Modal/ConfirmDrawerModal';
import { ReadingText } from '@doltech/ui/lib/figma/Typography';
import { SkillEnum, zIndex } from '@doltech/utils/lib/constants';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { FOOTER_MOBILE, HEADER_MOBILE } from '../layouts/BottomNavigationLayout';
import {
  GoToTimeReviewModal,
  ReviewTimeUpModal,
  SectionTimeUpModal,
  TimeUpModal,
} from '../layouts/TimeUpModal';
import { useTestSubmissionStore } from '../model/test-submissions.hook';
import { DO_TEST_MOBILE_FOOTER_HEIGHT } from './DoTest/Reading/Footer/DoTestFooter';

export interface TestSubmissionProps extends React.HTMLAttributes<HTMLDivElement> {
  onSubmit: () => void;
  disableSubmitAction?: boolean;
  background?: any;
  skill?: SkillEnum;
  onSubmitWithEvents?: () => void;
  enableEvent?: boolean;
}

interface MainProps {
  background: string;
  skill?: SkillEnum;
}

const Main = styled.div<MainProps>`
  background-color: ${(p) => p.background};
  .bottom-navigation {
    box-shadow: 0 -4px 4px rgba(0, 0, 0, 0.03);
  }

  &.has-background {
    background-color: ${colorsV2.black5};
  }
  #body-container,
  #passage-container {
    flex: 1;
    padding-top: ${HEADER_MOBILE}px;
    padding-bottom: ${FOOTER_MOBILE}px;
    background-color: ${colorsV2.black5};
    max-height: calc(100vh - ${HEADER_MOBILE}px);
    min-height: calc(100vh - ${HEADER_MOBILE}px);
    overflow: auto;
    > .dol-editor {
      padding: 16px;
    }
    display: none;
    &.visible {
      display: block;
    }
  }

  ${(p) =>
    p.skill === SkillEnum.LISTENING &&
    css`
      #body-container {
        overflow: hidden;
        max-height: unset;
      }
    `}

  .layout-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: ${zIndex.HEADER};
    background-color: ${colorsV2.white100};
    box-shadow: inset 0 -1px 0 ${colorsV2.gray10};
  }

  .layout-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${zIndex.FOOTER};
    background-color: ${colorsV2.white100};
  }

  .audio-player-wrapper {
    position: fixed;
    left: 0;
    right: 0;
    bottom: ${DO_TEST_MOBILE_FOOTER_HEIGHT - 10}px;
    z-index: ${zIndex.AUDIO_PLAYER_WRAPPER};
  }
`;

const UnansweredText = styled.span`
  color: ${figmaColors.primary5};
  font-weight: bold;
`;

export const TestMobileSubmission = observer((props: TestSubmissionProps) => {
  const {
    onSubmit,
    onSubmitWithEvents,
    enableEvent,
    children,
    disableSubmitAction,
    background = colorsV2.gray10,
    skill,
  } = props;
  const testSubmissionStore = useTestSubmissionStore();

  const unansweredCount = testSubmissionStore.totalQuestions - testSubmissionStore.answeredCount;
  const currentSection = testSubmissionStore.currentTestSection;

  if (isNil(currentSection) || testSubmissionStore.initialed === false) return null;
  return disableSubmitAction ? (
    <Main skill={skill} background={background}>
      {children}
    </Main>
  ) : (
    <Main skill={skill} background={background}>
      {children}
      {/* Modal when time up */}
      <TimeUpModal
        onTimeout={() => {
          if (enableEvent) {
            onSubmitWithEvents();
          } else {
            onSubmit();
          }
          testSubmissionStore.closeTimeUpModal();
        }}
      />
      {/* Modal when click submit test complete */}

      <ConfirmDrawerModal
        title="Submit !!"
        onCancel={testSubmissionStore.closeSubmitModal}
        onOk={onSubmit}
        icon={<IconModalSent style={{ fontSize: 72 }} />}
        okButtonText="Submit now"
        visible={testSubmissionStore.submitModalVisible}
        cancelButtonText="Double check"
      >
        <ReadingText block>Nice !! You have done a really great job out there!</ReadingText>
        <ReadingText block>Submit it now !</ReadingText>
      </ConfirmDrawerModal>
      {/* Modal when click submit test in-complete */}
      <ConfirmDrawerModal
        title="Submit !!"
        onCancel={testSubmissionStore.closeSubmitModal}
        onOk={onSubmit}
        isReverse
        icon={<IconModalSent style={{ fontSize: 72 }} />}
        cancelButtonText="Keep working"
        visible={testSubmissionStore.submitWarnModalVisible}
        okButtonText="Submit anyway"
      >
        <ReadingText block>
          You have <UnansweredText>({unansweredCount})</UnansweredText> undone questions.
        </ReadingText>
        <ReadingText block>Try to finish them.</ReadingText>
      </ConfirmDrawerModal>
      <GoToTimeReviewModal />
      <SectionTimeUpModal />
      <ReviewTimeUpModal onTimeout={onSubmit} />
    </Main>
  );
});
